/**
 * Zoho Payments checkout widget, shared by Add Credits and Renew Plan.
 * The widget opens on top of the current page — no new tab or window.
 */

const ZPAY_SCRIPT = "https://static.zohocdn.com/zpay/zpay-js/v1/zpayments.js";
let zpayLoader = null;

const loadZPayments = () => {
  if (window.ZPayments) return Promise.resolve();
  if (!zpayLoader) {
    zpayLoader = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = ZPAY_SCRIPT;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => {
        zpayLoader = null;
        script.remove();
        reject(new Error("Payment window could not be loaded"));
      };
      document.head.appendChild(script);
    });
  }
  return zpayLoader;
};

/**
 * Zoho's script never removes an old widget's message listener or iframe, and
 * every widget iframe shares one origin — so a later checkout would receive
 * the earlier payment's result. Take the old widget apart completely.
 */
const disposeWidget = (instance) => {
  try {
    instance.close();
  } catch (e) {
    /* already closed */
  }
  try {
    instance.bus?.destroy?.();
    if (instance.bus) instance.bus.listeners = {};
    instance.dom?.frameElement?.remove?.();
    instance.dom?.wrapperElement?.remove?.();
  } catch (e) {
    /* best effort */
  }
  document.querySelectorAll("#zpay-wrapper, #zpay-backdrop").forEach((el) =>
    el.remove()
  );
  document.body.style.overflow = "";
};

/**
 * Opens Zoho's checkout widget on top of this page (no new tab or window) and
 * resolves with the payment id when the clinic completes the payment.
 */
export const runCheckoutWidget = async (widget) => {
  await loadZPayments();

  const instance = new window.ZPayments({
    account_id: widget.accountId,
    domain: widget.domain,
    otherOptions: { api_key: widget.apiKey },
  });

  const address = {};
  if (widget.customer?.name) address.name = widget.customer.name;
  if (widget.customer?.email) address.email = widget.customer.email;
  if (widget.customer?.phone) address.phone = widget.customer.phone;

  try {
    const result = await instance.requestPaymentMethod({
      amount: Number(widget.amount).toFixed(2),
      currency_code: widget.currency || "INR",
      currency_symbol: "₹",
      payments_session_id: widget.sessionId,
      business: widget.business,
      description: widget.description,
      reference_number: widget.reference,
      address,
    });
    return { paymentId: result?.payment_id, signature: result?.signature };
  } finally {
    disposeWidget(instance);
  }
};

export const WIDGET_ERRORS = {
  session_expired: "This payment session expired. Please try again.",
  invalid_payment_session: "This payment could not be started. Please try again.",
};

